import React, { useRef, useContext, useEffect, useState } from "react";
import styled from "styled-components";
import gql from "graphql-tag";
import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  useMutation,
  useQuery,
} from "@apollo/client";

import { setContext } from "@apollo/client/link/context";

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem("token");
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `${token}` : "",
    },
  };
});

const httpLink = createHttpLink({
  uri:
    (window.ENV?.REACT_APP_PROJECT_SERVER ||
      process.env.REACT_APP_PROJECT_SERVER) + "/graphql",
});

const user_client = new ApolloClient({
  cache: new InMemoryCache(),
  link: authLink.concat(httpLink),
});

const LOGIN = gql`
  mutation Login($username: String!, $password: String!) {
    login(username: $username, password: $password) {
      token
      _id
    }
  }
`;

const LOGIN_WITH_TOKEN = gql`
  mutation LoginWithToken($token: String!) {
    loginWithToken(token: $token) {
      token
      _id
    }
  }
`;

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgb(14, 18, 25);
  opacity: 1;
  transition: 0.5s opacity;
  z-index: 100;
`;

const H1 = styled.h1`
  font-size: 2em;
  font-weight: 100;
`;
const Main = styled.div`
  width: 300px;
  margin-top: -24%;
`;
const Form = styled.form`
  display: flex;
  flex-direction: column;
`;
const Input = styled.input`
  background-color: #1f222b;
  border-radius: 4px;
  border: none;
  padding: 1em;
  margin-bottom: 1em;
  outline: none;
  color: #fff;
`;
const Button = styled.div`
  background-color: #c11938;
  padding: 1em;
  border-radius: 4px;
  min-width: 80px;
  display: flex;
  justify-content: center;
  cursor: pointer;
  margin-top: 2em;
`;
const Error = styled.div`
  color: #c11938;
  background-color: rgba(193, 25, 56, 0.08);
  border: 1px solid rgba(193, 25, 56, 0.22);
  padding: 0.75em 1em;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  margin-bottom: 1em;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  max-width: 100%;
  text-align: center;
  line-height: 1.2;
  &::before {
    content: "⚠";
    margin-right: 0.5em;
    font-size: 1.05em;
  }
`;
export default function Login({ loggedIn }) {
  const [login, { data }] = useMutation(LOGIN, {
    client: user_client,
    onCompleted: (data) => {
      localStorage.setItem("user-id", data.login._id);
      localStorage.setItem("token", data.login.token);
      loggedIn();
    },
    onError: (err) => {
      console.error("Login error", err);
      const msg =
        (err &&
          err.graphQLErrors &&
          err.graphQLErrors[0] &&
          err.graphQLErrors[0].message) ||
        err.message ||
        "Login failed";
      setErrorMsg(msg);
    },
  });
  const [loginWithToken, { data: token_data }] = useMutation(LOGIN_WITH_TOKEN, {
    client: user_client,
    onCompleted: (data) => {
      localStorage.setItem("user-id", data.loginWithToken._id);
      localStorage.setItem("token", data.loginWithToken.token);
      loggedIn();
    },
    onError: (err) => {
      console.error("Token login error", err);
      const msg =
        (err &&
          err.graphQLErrors &&
          err.graphQLErrors[0] &&
          err.graphQLErrors[0].message) ||
        err.message ||
        "Token login failed";
      setErrorMsg(msg);
    },
  });
  const usernameRef = useRef();
  const passwordRef = useRef();
  const buttonRef = useRef();
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let callback = (event) => {
      try {
        let data = JSON.parse(event?.data);
        if (data?.event === "login") {
          loginWithToken({ variables: { token: data.data.token } });
        }
      } catch (err) {}
    };
    window.addEventListener("message", callback);
    return () => {
      window.removeEventListener("message", callback);
    };
  }, []);

  return (
    <Container>
      <Main>
        <H1>Welcome</H1>

        <Form>
          {errorMsg && <Error>{errorMsg}</Error>}
          <Input
            ref={usernameRef}
            placeholder={"Username"}
            tabIndex="1"
            onChange={() => setErrorMsg("")}
          />
          <Input
            tabIndex="2"
            ref={passwordRef}
            placeholder={"Password"}
            type={"password"}
            autoComplete="current-password"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setErrorMsg("");
                login({
                  variables: {
                    username: usernameRef.current.value,
                    password: passwordRef.current.value,
                  },
                });
              }
            }}
            onChange={() => setErrorMsg("")}
          />
          <Button
            tabIndex="3"
            ref={buttonRef}
            onClick={() => {
              setErrorMsg("");
              login({
                variables: {
                  username: usernameRef.current.value,
                  password: passwordRef.current.value,
                },
              });
            }}
          >
            Login
          </Button>
        </Form>
      </Main>
    </Container>
  );
}
